import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, ThemeProvider } from '@mui/material';
import { Icons, THEMES, createTheme } from 'greener-ui-library';
import { useAmplitude } from 'hooks/useAmplitude';
import { usePdfExport } from 'hooks/usePdfExport';
import { PDF_HEIGHT, PDF_WIDTH } from 'hooks/usePdfExport/usePdfExport';
import { enqueueSnackbar } from 'notistack';
import { replaceSvgImgsWithPng } from 'utils/image.utils';
import { PdfLayout, PdfLayoutProps } from './PdfLayout';

export function PdfExportButton({
  filename = 'Solution',
  title,
  ...props
}: PdfLayoutProps & { filename: string; title: string }) {
  const { t } = useTranslation();
  const pdfRef = useRef<HTMLDivElement>(null);
  const { downloadPdf, isLoading } = usePdfExport();
  const { logEvent } = useAmplitude();
  const [isExportRendering, setIsExportRendering] = useState(false);

  const lightTheme = createTheme({ theme: THEMES.LIGHT });

  const handleExport = async () => {
    setIsExportRendering(true);

    try {
      // let React mount + layout settle
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      await new Promise((r) => requestAnimationFrame(() => r(null)));

      if (pdfRef.current) {
        await replaceSvgImgsWithPng(pdfRef.current);
      }

      await downloadPdf(pdfRef, `${filename}.pdf`);
      logEvent({ eventType: 'solution_pdf_downloaded' });
      enqueueSnackbar(t('blueprints:share.pdfDownloadSuccess'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('blueprints:share.errors.pdfDownloadFailed'), { variant: 'error' });
    } finally {
      setIsExportRendering(false);
    }
  };

  return (
    <>
      {isExportRendering ? (
        <div
          style={{
            position: 'fixed',
            left: '-9999px',
          }}
        >
          <ThemeProvider theme={lightTheme}>
            <Box
              ref={pdfRef}
              sx={{ width: PDF_WIDTH, minHeight: PDF_HEIGHT * 2, background: (theme) => theme.palette.grey[100] }}
            >
              <PdfLayout {...props} />
            </Box>
          </ThemeProvider>
        </div>
      ) : null}

      <Button
        variant="outlined"
        onClick={handleExport}
        disabled={isExportRendering || isLoading}
        sx={{ flex: '0 0 auto' }}
        endIcon={
          isExportRendering || isLoading ? (
            <CircularProgress size={16} />
          ) : (
            <Icons.FileDownload sx={{ width: '16px', height: '16px' }} />
          )
        }
      >
        {title}
      </Button>
    </>
  );
}
